import React, { useEffect, useRef, useState } from 'react';
import { DotLoading, NavBar, Popup } from 'antd-mobile';
import { Html5Qrcode } from 'html5-qrcode';
import { CloseOutline } from 'antd-mobile-icons';
import styled from 'styled-components';

const ScanWrapper = styled.div`
	width: 100%;
	height: 100%;
	display: flex;
	flex-flow: column;
`;

const ContentWrapper = styled.div`
	align-items: center;
	justify-content: center;
	height: 100%;
	display: flex;
	background: #f0f2f5;
`;

const ReaderWrapper = styled.div`
	width: 100%;
`;

const Nav = styled(NavBar)`
	background: #fff;
	display: flex;
	border-bottom: solid 1px var(--adm-color-border);
`;

function ScanPopup(props) {
	const {
		visible,
		onClose,
		onScan
	} = props;

	const readerID = useRef(Date.now().toString(32));
	const html5QrcodeScanner = useRef(null);
	const [loading, setLoading] = useState(false);

	function onScanSuccess(decodedText, decodedResult) {
		onScan(decodedText, decodedResult);
	}

	function onScanFailure() {
	}

	function getQrbox(viewfinderWidth, viewfinderHeight) {
		const minEdge = Math.min(viewfinderWidth, viewfinderHeight);
		const base = Math.floor(minEdge * 0.6);
		const rawWidth = Math.floor(base * 1.15);
		const rawHeight = base;
		const maxWidth = Math.max(100, viewfinderWidth - 24);
		const maxHeight = Math.max(100, viewfinderHeight - 24);

		return {
			width: Math.max(100, Math.min(rawWidth, maxWidth)),
			height: Math.max(100, Math.min(rawHeight, maxHeight))
		};
	}

	async function applyPreferredFocusMode() {
		if (!html5QrcodeScanner.current) {
			return;
		}

		try {
			const capabilities = html5QrcodeScanner.current.getRunningTrackCapabilities();
			const focusModes = Array.isArray(capabilities?.focusMode) ? capabilities.focusMode : [];

			if (focusModes.includes('continuous')) {
				await html5QrcodeScanner.current.applyVideoConstraints({
					advanced: [{ focusMode: 'continuous' }]
				});
			} else if (focusModes.includes('single-shot')) {
				await html5QrcodeScanner.current.applyVideoConstraints({
					advanced: [{ focusMode: 'single-shot' }]
				});
			}
		} catch (err) {
			console.log('Focus mode not supported:', err);
		}
	}

	async function stopCamera() {
		if (!html5QrcodeScanner.current) {
			return;
		}

		try {
			if (html5QrcodeScanner.current.getState() === 2) {
				await html5QrcodeScanner.current.stop();
			}
		} catch (err) {
			console.log('Failed to stop scanner:', err);
		}

		try {
			html5QrcodeScanner.current.clear();
		} catch (err) {
			console.log('Failed to clear scanner:', err);
		}

		html5QrcodeScanner.current = null;
	}

	async function startCamera() {
		if (html5QrcodeScanner.current && html5QrcodeScanner.current.getState() === 2) {
			return;
		}

		setLoading(true);
		html5QrcodeScanner.current = new Html5Qrcode(readerID.current);

		try {
			await html5QrcodeScanner.current.start(
				{ facingMode: 'environment' },
				{
					fps: 10,
					qrbox: getQrbox
				},
				onScanSuccess,
				onScanFailure
			);

			await applyPreferredFocusMode();
		} catch (err) {
			console.log('Failed to start scanner:', err);
		} finally {
			setLoading(false);
		}
	}

	useEffect(() => {
		if (visible) {
			startCamera();
		} else {
			stopCamera();
		}

		return () => {
			stopCamera();
		};
	}, [visible]);

	return (
		<Popup
			style={{ zIndex: 1001 }}
			position='right'
			visible={visible}
			forceRender
			destroyOnClose
			bodyStyle={{ width: '100vw' }}
			onClose={onClose}
		>
			<ScanWrapper>
				<Nav
					backArrow={<CloseOutline />}
					onBack={onClose}
					back=''
					right={loading && <DotLoading />}
				>
					Scan Barcode / QRCode
				</Nav>
				<ContentWrapper>
					<ReaderWrapper id={readerID.current} />
				</ContentWrapper>
			</ScanWrapper>
		</Popup>
	);
}

export default ScanPopup;
